import {
  Bell,
  Bot,
  CalendarClock,
  Clock,
  MessageSquareText,
  Send,
  TriangleAlert,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { isStaffReminderTemplate, reminderEmailKind } from '@notifi/shared-types/email-kinds';
import { Badge } from '@/components/atoms/badge';
import { EMAIL_KIND_LABEL } from '@/types/emailTemplate';
import { DetailCard, DetailField, DetailFieldGrid } from '@/components/molecules/DetailCard';
import { cn } from '@/lib/utils';
import type { CadenceStep, ReminderPolicy, TemplateConfig } from '@/types/reminder-policy';
import { cadenceReachability, type UnreachableReason } from './cadenceReachability';

interface ReminderPolicyReadOnlyViewProps {
  policy: ReminderPolicy;
}

export function ReminderPolicyReadOnlyView({ policy }: ReminderPolicyReadOnlyViewProps) {
  // The policy endpoint historically returned `cadence` and `templates` as
  // JSON columns. A partially migrated tenant can therefore briefly receive
  // an object instead of the array the UI expects. Keep the settings screen
  // readable while the user fixes the policy rather than letting one malformed
  // field take down the entire Reviews workspace.
  const cadence = Array.isArray(policy.cadence) ? policy.cadence : [];
  const templates = Array.isArray(policy.templates) ? policy.templates : [];
  const channels = [...new Set(cadence.map((step) => step.channel))];
  const reachability = cadenceReachability({ ...policy, cadence });
  const truncatedCount = reachability.filter((r) => r === 'truncated').length;
  const voiceOffCount = reachability.filter((r) => r === 'voice-off').length;
  const aiEnabledCount = [
    policy.aiPersonalization,
    policy.aiSendTimeOptimization,
    policy.aiChannelSelection,
    policy.aiVoiceFallback,
  ].filter(Boolean).length;

  return (
    <div className="space-y-5">
      <DetailCard
        label="Sending schedule"
        icon={CalendarClock}
        description="The order and timing used for review requests."
      >
        {cadence.length === 0 ? (
          <p className="text-sm text-muted-foreground">No follow-up steps have been configured.</p>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border/80">
              {cadence.map((step, index) => (
                <CadenceRow
                  key={`${step.channel}-${step.offsetMinutes}-${index}`}
                  step={step}
                  templates={templates}
                  unreachable={reachability[index] ?? null}
                />
              ))}
            </div>
            {truncatedCount > 0 ? (
              <p className="mt-3 flex items-start gap-2 text-[13px] leading-5 text-amber-700 dark:text-amber-400">
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>
                  Max attempts is {policy.maxAttempts}, so the{' '}
                  {truncatedCount === 1 ? 'step' : `${truncatedCount} steps`} past it
                  {truncatedCount === 1 ? ' never sends' : ' never send'}. Raise max attempts or
                  remove {truncatedCount === 1 ? 'it' : 'them'}.
                </span>
              </p>
            ) : null}
            {voiceOffCount > 0 ? (
              <p className="mt-3 flex items-start gap-2 text-[13px] leading-5 text-amber-700 dark:text-amber-400">
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>
                  Voice fallback is off, so the{' '}
                  {voiceOffCount === 1 ? 'voice step' : `${voiceOffCount} voice steps`}
                  {voiceOffCount === 1 ? ' never sends' : ' never send'}. Turn voice fallback on or
                  remove {voiceOffCount === 1 ? 'it' : 'them'}.
                </span>
              </p>
            ) : null}
          </>
        )}
      </DetailCard>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.3fr)_minmax(19rem,0.7fr)]">
        <DetailCard
          label="Message templates"
          icon={MessageSquareText}
          description="SMS and voice copy per step. Customer emails are designed in Designs → Emails."
          bodyClassName={templates.length > 0 ? 'max-h-[28rem] overflow-y-auto pr-1' : ''}
        >
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No templates configured.</p>
          ) : (
            <div className="space-y-2">
              {templates.map((template, index) => (
                <TemplateRow
                  key={`${template.key}-${template.channel}-${index}`}
                  template={template}
                  reminderType={policy.type}
                />
              ))}
            </div>
          )}
        </DetailCard>

        <div className="space-y-5">
          <DetailCard label="Delivery controls" icon={Bell}>
            <DetailFieldGrid>
              <DetailField label="Status" value={<BooleanBadge enabled={policy.enabled} />} />
              <DetailField label="Max attempts" value={policy.maxAttempts} />
              <DetailField label="Cadence steps" value={cadence.length} />
              <DetailField
                label="Channels"
                value={
                  channels.length > 0 ? (
                    <div className="flex flex-wrap justify-end gap-1">
                      {channels.map((channel) => (
                        <Badge key={channel} variant="outline">
                          {channel}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    '-'
                  )
                }
              />
            </DetailFieldGrid>
          </DetailCard>

          <DetailCard label="Quiet hours" icon={Clock}>
            <DetailFieldGrid>
              <DetailField
                label="Status"
                value={<BooleanBadge enabled={Boolean(policy.quietHours)} enabledLabel="Enabled" />}
              />
              <DetailField
                label="Window"
                value={
                  policy.quietHours
                    ? `${formatClockMinute(policy.quietHours.startMinute)} to ${formatClockMinute(
                        policy.quietHours.endMinute,
                      )}`
                    : 'None'
                }
              />
              <DetailField label="Timezone" value={policy.quietHours?.tz ?? '-'} />
            </DetailFieldGrid>
          </DetailCard>

          <DetailCard label="AI assistance" icon={Bot}>
            <DetailFieldGrid>
              <DetailField
                label="Personalization"
                value={<BooleanBadge enabled={policy.aiPersonalization} />}
              />
              <DetailField
                label="Send time"
                value={<BooleanBadge enabled={policy.aiSendTimeOptimization} />}
              />
              <DetailField
                label="Channel selection"
                value={<BooleanBadge enabled={policy.aiChannelSelection} />}
              />
              <DetailField
                label="Voice fallback"
                value={<BooleanBadge enabled={policy.aiVoiceFallback} />}
              />
              <DetailField label="Enabled features" value={`${aiEnabledCount} of 4`} />
            </DetailFieldGrid>
          </DetailCard>
        </div>
      </div>
    </div>
  );
}

/**
 * On/off as a dot plus near-neutral text rather than a filled pill.
 *
 * Six of these stack down one card. As solid black-on-white pills they read as
 * six buttons; as dots they read as a column of state, which is the argument
 * `integration-tokens.ts` already makes for the Connected Apps list. Same
 * words, same values — only the weight of the ink changed.
 */
function BooleanBadge({
  enabled,
  enabledLabel = 'On',
}: {
  enabled: boolean;
  enabledLabel?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 text-sm font-medium leading-6',
        enabled ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'size-1.5 rounded-full',
          enabled ? 'bg-emerald-500' : 'bg-muted-foreground/40',
        )}
        aria-hidden
      />
      {enabled ? enabledLabel : 'Off'}
    </span>
  );
}

function CadenceRow({
  step,
  templates,
  unreachable = null,
}: {
  step: CadenceStep;
  templates: TemplateConfig[];
  /** Why the engine never sends this step, or null when it does. */
  unreachable?: UnreachableReason | null;
}) {
  const template = templates.find(
    (candidate) => candidate.channel === step.channel && candidate.key === step.templateKey,
  );

  return (
    <div
      className={cn(
        'grid gap-3 border-b px-3 py-2.5 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center',
        // Dimmed rather than hidden: the step IS configured, and hiding it
        // would make the count disagree with the list. It just never fires.
        unreachable && 'bg-muted/30 opacity-60',
      )}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {formatOffset(step.offsetMinutes)}
          {unreachable ? (
            <span className="ml-2 text-xs font-normal text-amber-700 dark:text-amber-400">
              Never sends
            </span>
          ) : null}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          Template: {template ? humanizeKey(template.key) : step.templateKey || 'Default fallback'}
        </p>
        {unreachable === 'voice-off' ? (
          <p className="text-xs text-muted-foreground">Voice fallback is off</p>
        ) : null}
      </div>
      <Badge variant="outline" className="w-fit">
        {step.channel}
      </Badge>
      {/* The send icon is a promise. Not for a step that cannot keep it. */}
      {unreachable ? (
        <span className="hidden size-4 sm:block" aria-hidden />
      ) : (
        <Send className="hidden size-4 text-muted-foreground sm:block" />
      )}
    </div>
  );
}

function humanizeKey(key: string) {
  return key
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function TemplateRow({
  template,
  reminderType,
}: {
  template: TemplateConfig;
  reminderType: string;
}) {
  // A customer EMAIL step renders through its email design — the copy shown
  // here would be misleading, so point at the design instead.
  const designKind =
    template.channel === 'EMAIL' ? reminderEmailKind(reminderType, template.key) : null;
  const staffAudience =
    template.channel === 'EMAIL' && isStaffReminderTemplate(reminderType, template.key);
  return (
    <div className="rounded-md border px-3 py-2.5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="truncate text-sm font-medium">{template.key}</p>
        <div className="flex shrink-0 items-center gap-1">
          {designKind ? <Badge variant="secondary">Designed email</Badge> : null}
          {staffAudience ? <Badge variant="secondary">Goes to your team</Badge> : null}
          <Badge variant="outline">{template.channel}</Badge>
        </div>
      </div>
      {designKind ? (
        <p className="text-xs leading-5 text-muted-foreground">
          Sends the {EMAIL_KIND_LABEL[designKind]} design.{' '}
          <Link
            to="/dashboard/settings/document-templates"
            search={{ tab: 'emails', kind: designKind }}
            className="font-medium text-primary hover:underline"
          >
            Open in Designs → Emails
          </Link>
        </p>
      ) : null}
      {!designKind && template.subject ? (
        <p className="truncate text-xs text-muted-foreground">Subject: {template.subject}</p>
      ) : null}
      {!designKind ? (
        <p className="max-h-12 overflow-hidden text-xs leading-5 text-muted-foreground">
          {template.body || 'No body configured.'}
        </p>
      ) : null}
    </div>
  );
}

function formatOffset(offsetMinutes: number) {
  if (offsetMinutes === 0) return 'At trigger time';

  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  if (abs % (24 * 60) === 0) {
    return `${sign}${abs / (24 * 60)} day${abs === 24 * 60 ? '' : 's'}`;
  }
  if (abs % 60 === 0) {
    return `${sign}${abs / 60} hour${abs === 60 ? '' : 's'}`;
  }
  return `${sign}${abs} minute${abs === 1 ? '' : 's'}`;
}

function formatClockMinute(minute: number) {
  const safeMinute = Math.max(0, Math.min(1439, Math.trunc(minute)));
  const hour24 = Math.floor(safeMinute / 60);
  const minutes = safeMinute % 60;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
}
